/**
 * Copyright (c) SAGE3 Development Team 2022. All Rights Reserved
 * University of Hawaii, University of Illinois Chicago, Virginia Tech
 *
 * Distributed under the terms of the SAGE3 License.  The full license is in
 * the file LICENSE, distributed as part of this software.
 */

import { useEffect, useRef, useState } from 'react';
import { Box, ButtonGroup, IconButton, Tooltip, VStack, HStack, Input, Select } from '@chakra-ui/react';
import {
  MdRectangle,
  MdCircle,
  MdTextFields,
  MdImage,
  MdUndo,
  MdRedo,
  MdDelete,
  MdRemove,
  MdArrowForward,
  MdFormatListBulleted,
  MdFlipToFront,
  MdFlipToBack,
  MdPictureAsPdf,
} from 'react-icons/md';
import { fabric } from 'fabric';
import * as pdfjsLib from 'pdfjs-dist';

import { state as AppState } from './';
import { App, AppGroup } from '../../schema';
import { AppWindow } from '../../components';
import { useAppStore, useUIStore, usePresenceStore, useUser } from '@sage3/frontend';

/**
 * SlideCreator SAGE3 application
 * Canvas-based slide builder using Fabric.js
 *
 * Features:
 * - Visual canvas for dragging/placing elements
 * - Text boxes (draggable, resizable, formatting)
 * - Shapes (rectangles, circles, lines, arrows)
 * - Images (upload + placement)
 * - Styling controls (colors, borders, fonts)
 * - Real-time sync via Yjs
 *
 * @param {AppSchema} props
 * @returns {JSX.Element}
 */
function AppComponent(props: App): JSX.Element {
  const s = props.data.state as AppState;
  const updateState = useAppStore((state) => state.updateState);

  // Check presentation mode (same pattern as Frame app)
  const localPresentationMode = useUIStore((s: any) => s.presentationMode);
  const { user } = useUser();
  const presences = usePresenceStore((s: any) => s.presences);

  const myPresence = presences.find((p: any) => p._id === user?._id);
  const followingUserId = myPresence?.data?.following;
  const followedUserPresence = followingUserId
    ? presences.find((p: any) => p._id === followingUserId)
    : null;
  const followedUserInPresentation = followedUserPresence?.data?.presentationMode || false;

  const presentationMode = localPresentationMode || followedUserInPresentation;

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fabricCanvasRef = useRef<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const [selectedObject, setSelectedObject] = useState<any>(null);
  const [fillColor, setFillColor] = useState('#3182CE');
  const [strokeColor, setStrokeColor] = useState('#000000');
  const [fontFamily, setFontFamily] = useState('Arial');
  const [fontSize, setFontSize] = useState(24);

  // Undo/Redo state
  const [history, setHistory] = useState<string[]>([]);
  const [historyStep, setHistoryStep] = useState<number>(-1);
  const isUndoingRef = useRef(false);
  const isLocalUpdateRef = useRef(false);

  // Initialize Fabric.js canvas
  useEffect(() => {
    if (!canvasRef.current || fabricCanvasRef.current) return;

    // Configure PDF.js worker - use unpkg CDN with HTTPS
    pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

    const canvas = new fabric.Canvas(canvasRef.current, {
      width: props.data.size.width,
      height: props.data.size.height,
      backgroundColor: s.backgroundColor,
    });

    fabricCanvasRef.current = canvas;

    // Load existing canvas data
    if (s.canvasData) {
      try {
        canvas.loadFromJSON(JSON.parse(s.canvasData), () => {
          canvas.renderAll();
        });
      } catch (e) {
        console.error('Failed to load canvas data:', e);
      }
    }

    // Handle object selection
    canvas.on('selection:created', (e: any) => {
      setSelectedObject(e.selected?.[0]);
    });

    canvas.on('selection:updated', (e: any) => {
      setSelectedObject(e.selected?.[0]);
    });

    canvas.on('selection:cleared', () => {
      setSelectedObject(null);
    });

    // Handle canvas modifications
    canvas.on('object:modified', saveCanvasState);
    canvas.on('object:added', saveCanvasState);
    canvas.on('object:removed', saveCanvasState);

    return () => {
      if (fabricCanvasRef.current) {
        fabricCanvasRef.current.dispose();
        fabricCanvasRef.current = null;
      }
    };
  }, []);

  // Resize canvas when app size changes
  useEffect(() => {
    if (fabricCanvasRef.current) {
      fabricCanvasRef.current.setWidth(props.data.size.width);
      fabricCanvasRef.current.setHeight(props.data.size.height);
      fabricCanvasRef.current.renderAll();
    }
  }, [props.data.size.width, props.data.size.height]);

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && e.key === 'z') {
        e.preventDefault();
        undo();
      } else if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'z') {
        e.preventDefault();
        redo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [historyStep, history]);

  // Load canvas data when it changes from remote
  useEffect(() => {
    if (fabricCanvasRef.current && s.canvasData && !isLocalUpdateRef.current) {
      try {
        fabricCanvasRef.current.loadFromJSON(JSON.parse(s.canvasData), () => {
          fabricCanvasRef.current.renderAll();
        });
      } catch (e) {
        console.error('Failed to load canvas data:', e);
      }
    }
  }, [s.canvasData]);

  // Save canvas state to SAGE3 and history
  const saveCanvasState = () => {
    if (fabricCanvasRef.current && !isUndoingRef.current) {
      isLocalUpdateRef.current = true;
      const json = JSON.stringify(fabricCanvasRef.current.toJSON());
      updateState(props._id, { canvasData: json });

      // Save to history (truncate future history if we're in the middle)
      const newHistory = history.slice(0, historyStep + 1);
      newHistory.push(json);

      // Limit history to 50 states
      if (newHistory.length > 50) {
        newHistory.shift();
      } else {
        setHistoryStep(historyStep + 1);
      }

      setHistory(newHistory);

      // Reset flag after state update
      setTimeout(() => {
        isLocalUpdateRef.current = false;
      }, 100);
    }
  };

  // Undo
  const undo = () => {
    if (historyStep > 0 && fabricCanvasRef.current) {
      isUndoingRef.current = true;
      const prevStep = historyStep - 1;
      const prevState = history[prevStep];

      fabricCanvasRef.current.loadFromJSON(JSON.parse(prevState), () => {
        fabricCanvasRef.current.renderAll();
        updateState(props._id, { canvasData: prevState });
        setHistoryStep(prevStep);
        isUndoingRef.current = false;
      });
    }
  };

  // Redo
  const redo = () => {
    if (historyStep < history.length - 1 && fabricCanvasRef.current) {
      isUndoingRef.current = true;
      const nextStep = historyStep + 1;
      const nextState = history[nextStep];

      fabricCanvasRef.current.loadFromJSON(JSON.parse(nextState), () => {
        fabricCanvasRef.current.renderAll();
        updateState(props._id, { canvasData: nextState });
        setHistoryStep(nextStep);
        isUndoingRef.current = false;
      });
    }
  };

  // Add rectangle
  const addRectangle = () => {
    const rect = new fabric.Rect({
      left: 100,
      top: 100,
      width: 200,
      height: 100,
      fill: fillColor,
      stroke: strokeColor,
      strokeWidth: 2,
    });
    fabricCanvasRef.current?.add(rect);
    fabricCanvasRef.current?.setActiveObject(rect);
  };

  // Add circle
  const addCircle = () => {
    const circle = new fabric.Circle({
      left: 150,
      top: 150,
      radius: 50,
      fill: fillColor,
      stroke: strokeColor,
      strokeWidth: 2,
    });
    fabricCanvasRef.current?.add(circle);
    fabricCanvasRef.current?.setActiveObject(circle);
  };

  // Add text
  const addText = () => {
    const text = new fabric.IText('Double-click to edit', {
      left: 100,
      top: 200,
      fontFamily: fontFamily,
      fontSize: fontSize,
      fill: fillColor,
      lockScalingX: true,
      lockScalingY: true,
    });
    fabricCanvasRef.current?.add(text);
    fabricCanvasRef.current?.setActiveObject(text);
  };

  // Add line
  const addLine = () => {
    const line = new fabric.Line([50, 100, 250, 100], {
      stroke: strokeColor,
      strokeWidth: 2,
    });
    fabricCanvasRef.current?.add(line);
    fabricCanvasRef.current?.setActiveObject(line);
  };

  // Add arrow
  const addArrow = () => {
    const line = new fabric.Line([50, 150, 250, 150], {
      stroke: strokeColor,
      strokeWidth: 2,
    });

    // Create arrowhead triangle
    const arrowHead = new fabric.Triangle({
      left: 250,
      top: 150,
      originX: 'center',
      originY: 'center',
      width: 15,
      height: 15,
      fill: strokeColor,
      angle: 90,
    });

    // Group line and arrowhead together
    const arrow = new fabric.Group([line, arrowHead], {
      left: 100,
      top: 150,
    });

    fabricCanvasRef.current?.add(arrow);
    fabricCanvasRef.current?.setActiveObject(arrow);
  };

  // Add bulleted list
  const addList = () => {
    const list = new fabric.IText('• Item 1\n• Item 2\n• Item 3', {
      left: 100,
      top: 250,
      fontFamily: fontFamily,
      fontSize: fontSize,
      fill: fillColor,
      lineHeight: 1.5,
      lockScalingX: true,
      lockScalingY: true,
    });
    fabricCanvasRef.current?.add(list);
    fabricCanvasRef.current?.setActiveObject(list);
  };

  // Trigger file input for image upload
  const triggerImageUpload = () => {
    fileInputRef.current?.click();
  };

  // Handle image file selection
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !fabricCanvasRef.current) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const imgData = event.target?.result as string;
      fabric.Image.fromURL(imgData, (img) => {
        // Scale image to reasonable size if too large
        const maxWidth = 300;
        const maxHeight = 300;
        if (img.width! > maxWidth || img.height! > maxHeight) {
          const scale = Math.min(maxWidth / img.width!, maxHeight / img.height!);
          img.scale(scale);
        }

        img.set({
          left: 100,
          top: 100,
        });

        fabricCanvasRef.current?.add(img);
        fabricCanvasRef.current?.setActiveObject(img);
        fabricCanvasRef.current?.renderAll();
        saveCanvasState();
      });
    };
    reader.readAsDataURL(file);

    // Reset file input
    e.target.value = '';
  };

  // Trigger file input for PDF upload
  const triggerPdfUpload = () => {
    pdfInputRef.current?.click();
  };

  // Handle PDF file selection (first page only)
  const handlePdfUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !fabricCanvasRef.current) return;

    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const page = await pdf.getPage(1); // Get first page only

      // Render page to a temporary canvas
      const scale = 1.5;
      const viewport = page.getViewport({ scale });
      const tempCanvas = document.createElement('canvas');
      const context = tempCanvas.getContext('2d');

      if (!context) return;

      tempCanvas.height = viewport.height;
      tempCanvas.width = viewport.width;

      await page.render({
        canvasContext: context,
        viewport: viewport,
        canvas: tempCanvas,
      }).promise;

      // Convert canvas to image and add to Fabric canvas
      const imgData = tempCanvas.toDataURL();
      fabric.Image.fromURL(imgData, (img) => {
        // Scale to reasonable size
        const maxWidth = 400;
        const maxHeight = 400;
        if (img.width! > maxWidth || img.height! > maxHeight) {
          const scale = Math.min(maxWidth / img.width!, maxHeight / img.height!);
          img.scale(scale);
        }

        img.set({
          left: 100,
          top: 100,
        });

        fabricCanvasRef.current?.add(img);
        fabricCanvasRef.current?.setActiveObject(img);
        fabricCanvasRef.current?.renderAll();
        saveCanvasState();
      });
    } catch (error) {
      console.error('Failed to load PDF:', error);
    }

    // Reset file input
    e.target.value = '';
  };

  // Delete selected object
  const deleteSelected = () => {
    const activeObject = fabricCanvasRef.current?.getActiveObject();
    if (activeObject) {
      fabricCanvasRef.current?.remove(activeObject);
    }
  };

  // Bring to front
  const bringToFront = () => {
    const activeObject = fabricCanvasRef.current?.getActiveObject();
    if (activeObject) {
      fabricCanvasRef.current?.bringToFront(activeObject);
      fabricCanvasRef.current?.renderAll();
      saveCanvasState();
    }
  };

  // Send to back
  const sendToBack = () => {
    const activeObject = fabricCanvasRef.current?.getActiveObject();
    if (activeObject) {
      fabricCanvasRef.current?.sendToBack(activeObject);
      fabricCanvasRef.current?.renderAll();
      saveCanvasState();
    }
  };

  // Update selected object fill color
  useEffect(() => {
    if (selectedObject && fillColor) {
      selectedObject.set('fill', fillColor);
      fabricCanvasRef.current?.renderAll();
      saveCanvasState();
    }
  }, [fillColor]);

  // Update selected object stroke color
  useEffect(() => {
    if (selectedObject && strokeColor) {
      selectedObject.set('stroke', strokeColor);
      fabricCanvasRef.current?.renderAll();
      saveCanvasState();
    }
  }, [strokeColor]);

  return (
    <AppWindow app={props}>
      <Box w="100%" h="100%" position="relative" overflow="hidden">
        {/* Toolbar */}
        <VStack
          position="absolute"
          top={2}
          left={2}
          zIndex={1000}
          spacing={2}
          align="stretch"
          bg="gray.100"
          p={3}
          borderRadius="md"
          boxShadow="lg"
          border="1px solid"
          borderColor="gray.300"
          display={presentationMode ? 'none' : 'flex'}
        >
          <ButtonGroup size="sm" isAttached variant="outline">
            <Tooltip label="Undo (Cmd+Z)">
              <IconButton
                aria-label="Undo"
                icon={<MdUndo />}
                onClick={undo}
                isDisabled={historyStep <= 0}
              />
            </Tooltip>
            <Tooltip label="Redo (Cmd+Shift+Z)">
              <IconButton
                aria-label="Redo"
                icon={<MdRedo />}
                onClick={redo}
                isDisabled={historyStep >= history.length - 1}
              />
            </Tooltip>
          </ButtonGroup>

          <ButtonGroup size="sm" isAttached variant="outline">
            <Tooltip label="Add Rectangle">
              <IconButton
                aria-label="Add Rectangle"
                icon={<MdRectangle />}
                onClick={addRectangle}
              />
            </Tooltip>
            <Tooltip label="Add Circle">
              <IconButton
                aria-label="Add Circle"
                icon={<MdCircle />}
                onClick={addCircle}
              />
            </Tooltip>
            <Tooltip label="Add Text">
              <IconButton
                aria-label="Add Text"
                icon={<MdTextFields />}
                onClick={addText}
              />
            </Tooltip>
            <Tooltip label="Add Line">
              <IconButton
                aria-label="Add Line"
                icon={<MdRemove />}
                onClick={addLine}
              />
            </Tooltip>
            <Tooltip label="Add Arrow">
              <IconButton
                aria-label="Add Arrow"
                icon={<MdArrowForward />}
                onClick={addArrow}
              />
            </Tooltip>
            <Tooltip label="Add List">
              <IconButton
                aria-label="Add List"
                icon={<MdFormatListBulleted />}
                onClick={addList}
              />
            </Tooltip>
            <Tooltip label="Add Image">
              <IconButton
                aria-label="Add Image"
                icon={<MdImage />}
                onClick={triggerImageUpload}
              />
            </Tooltip>
            <Tooltip label="Add PDF (First Page)">
              <IconButton
                aria-label="Add PDF"
                icon={<MdPictureAsPdf />}
                onClick={triggerPdfUpload}
              />
            </Tooltip>
          </ButtonGroup>

          {/* Hidden file input for image upload */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleImageUpload}
          />

          {/* Hidden file input for PDF upload */}
          <input
            ref={pdfInputRef}
            type="file"
            accept="application/pdf"
            style={{ display: 'none' }}
            onChange={handlePdfUpload}
          />

          {selectedObject && (
            <>
              <VStack spacing={2} align="stretch">
                <Box>
                  <Box fontSize="xs" mb={1}>Fill/Text Color</Box>
                  <Input
                    type="color"
                    value={fillColor}
                    onChange={(e) => setFillColor(e.target.value)}
                    size="sm"
                    w="100%"
                    title="Fill/Text Color"
                  />
                </Box>
                {selectedObject.type !== 'i-text' && (
                  <Box>
                    <Box fontSize="xs" mb={1}>Stroke Color</Box>
                    <Input
                      type="color"
                      value={strokeColor}
                      onChange={(e) => setStrokeColor(e.target.value)}
                      size="sm"
                      w="100%"
                      title="Stroke Color"
                    />
                  </Box>
                )}
              </VStack>

              {selectedObject.type === 'i-text' && (
                <>
                  <Select
                    size="sm"
                    value={fontFamily}
                    onChange={(e) => {
                      setFontFamily(e.target.value);
                      selectedObject.set('fontFamily', e.target.value);
                      fabricCanvasRef.current?.renderAll();
                      saveCanvasState();
                    }}
                  >
                    <option value="Arial">Arial</option>
                    <option value="Times New Roman">Times New Roman</option>
                    <option value="Courier New">Courier New</option>
                    <option value="Georgia">Georgia</option>
                    <option value="Verdana">Verdana</option>
                  </Select>

                  <Input
                    type="number"
                    size="sm"
                    value={fontSize}
                    onChange={(e) => {
                      const size = parseInt(e.target.value);
                      setFontSize(size);
                      selectedObject.set('fontSize', size);
                      fabricCanvasRef.current?.renderAll();
                      saveCanvasState();
                    }}
                    min={8}
                    max={72}
                  />
                </>
              )}

              <HStack spacing={2}>
                <Tooltip label="Bring to Front">
                  <IconButton
                    aria-label="Bring to Front"
                    icon={<MdFlipToFront />}
                    onClick={bringToFront}
                    size="sm"
                  />
                </Tooltip>
                <Tooltip label="Send to Back">
                  <IconButton
                    aria-label="Send to Back"
                    icon={<MdFlipToBack />}
                    onClick={sendToBack}
                    size="sm"
                  />
                </Tooltip>
              </HStack>

              <IconButton
                aria-label="Delete"
                icon={<MdDelete />}
                onClick={deleteSelected}
                size="sm"
                colorScheme="red"
              />
            </>
          )}
        </VStack>

        {/* Canvas */}
        <canvas
          ref={canvasRef}
          style={{
            display: 'block',
            width: '100%',
            height: '100%',
            border: '1px solid #ccc',
          }}
        />
      </Box>
    </AppWindow>
  );
}

/**
 * Toolbar component for SlideCreator app
 */
function ToolbarComponent(_props: App): JSX.Element {
  return <></>;
}

/**
 * Grouped App toolbar component
 */
const GroupedToolbarComponent = (_props: { apps: AppGroup }) => {
  return <></>;
};

export default { AppComponent, ToolbarComponent, GroupedToolbarComponent };
